/**
 * Transforms backend dashboard data to match UI component expectations
 * Backend returns flat structure, UI expects nested structure with farmers object
 */
export function transformDashboardData(backendData) {
  if (!backendData || !Array.isArray(backendData)) {
    return [];
  }

  return backendData.map((item) => {
    // Extract village from address (assume it's the first part before comma, or use full address)
    const addressParts = (item.address || '').split(',');
    const village = addressParts[0]?.trim() || item.address || '';

    // Calculate soil health score (simplified - you may need to adjust this logic)
    const soil_health_score = calculateSoilHealthScore(item);

    // Calculate primary issue
    const primary_issue = determinePrimaryIssue(item);

    return {
      farmer_id: item.user_id || Math.random().toString(),
      farmers: {
        name: item.farmer_name || '',
        phone_number: item.phone_number || '',
        village: village,
        address: item.address || '',
      },
      sample_id: item.sample_id || '',
      farm_size: item.farm_size || '',
      previous_crop: item.previous_crop || '',
      next_crop: item.next_crop || '',
      // Map backend field names to UI field names
      ph: item.ph !== null && item.ph !== undefined ? parseFloat(item.ph) : null,
      ec: item.ec !== null && item.ec !== undefined ? parseFloat(item.ec) : null,
      organic_carbon: item.oc !== null && item.oc !== undefined ? parseFloat(item.oc) : null,
      oc: item.oc !== null && item.oc !== undefined ? parseFloat(item.oc) : null,
      nitrogen: item.nitrogen !== null && item.nitrogen !== undefined ? parseFloat(item.nitrogen) : null,
      nitrate: item.nitrate !== null && item.nitrate !== undefined ? parseFloat(item.nitrate) : null,
      ammonia: item.ammonia !== null && item.ammonia !== undefined ? parseFloat(item.ammonia) : null,
      magnesium: item.magnesium !== null && item.magnesium !== undefined ? parseFloat(item.magnesium) : null,
      phosphorus: item.phosphorus !== null && item.phosphorus !== undefined ? parseFloat(item.phosphorus) : null,
      potassium: item.potassium !== null && item.potassium !== undefined ? parseFloat(item.potassium) : null,
      calcium: item.calcium !== null && item.calcium !== undefined ? parseFloat(item.calcium) : null,
      zinc: item.zinc !== null && item.zinc !== undefined ? parseFloat(item.zinc) : null,
      iron: item.iron !== null && item.iron !== undefined ? parseFloat(item.iron) : null,
      boron: item.boron !== null && item.boron !== undefined ? parseFloat(item.boron) : null,
      copper: item.copper !== null && item.copper !== undefined ? parseFloat(item.copper) : null,
      sulphur: item.sulphur !== null && item.sulphur !== undefined ? parseFloat(item.sulphur) : null,
      sulfur: item.sulphur !== null && item.sulphur !== undefined ? parseFloat(item.sulphur) : null, // alias
      manganese: item.manganese !== null && item.manganese !== undefined ? parseFloat(item.manganese) : null,
      report_id: item.report_id || '',
      soil_health_score: soil_health_score,
      primary_issue: primary_issue,
    };
  });
}

/**
 * Calculate soil health score based on nutrient values
 * This is a simplified calculation - adjust based on actual requirements
 */
function calculateSoilHealthScore(item) {
  let score = 100;
  
  // Deduct points for low organic carbon
  if (item.oc !== null && item.oc < 0.5) {
    score -= 20;
  }
  
  // Deduct points for high pH
  if (item.ph !== null && item.ph > 8.5) {
    score -= 20;
  }
  
  // Deduct points for low potassium
  if (item.potassium !== null && item.potassium < 110) {
    score -= 15;
  }
  
  // Deduct points for high phosphorus
  if (item.phosphorus !== null && item.phosphorus > 50) {
    score -= 10;
  }
  
  // Deduct points for micronutrient deficiencies
  if (
    (item.zinc !== null && item.zinc < 0.6) ||
    (item.iron !== null && item.iron < 4.5) ||
    (item.boron !== null && item.boron < 0.5) ||
    (item.copper !== null && item.copper < 0.2)
  ) {
    score -= 15;
  }
  
  return Math.max(0, Math.min(100, score));
}

/**
 * Determine primary issue based on nutrient values
 */
function determinePrimaryIssue(item) {
  const issues = [];
  
  if (item.oc !== null && item.oc < 0.5) {
    issues.push({ type: 'Low OC dominant', severity: 3 });
  }
  
  if (item.ph !== null && item.ph > 8.5) {
    issues.push({ type: 'High pH dominant', severity: 3 });
  }
  
  if (
    (item.phosphorus !== null && item.phosphorus > 50) ||
    (item.potassium !== null && item.potassium < 110) ||
    (item.nitrogen !== null && item.nitrogen < 280)
  ) {
    issues.push({ type: 'Macro imbalance (NPK)', severity: 2 });
  }
  
  if (
    (item.zinc !== null && item.zinc < 0.6) ||
    (item.iron !== null && item.iron < 4.5) ||
    (item.boron !== null && item.boron < 0.5) ||
    (item.copper !== null && item.copper < 0.2)
  ) {
    issues.push({ type: 'Micronutrient deficiency dominant', severity: 2 });
  }
  
  if (issues.length === 0) {
    return 'Balanced/No major issue';
  }
  
  // Return the most severe issue
  issues.sort((a, b) => b.severity - a.severity);
  return issues[0].type;
}



